import { useEffect, useState } from "react";
import { ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { addrUrl } from "../lib/chains";
import { useApp } from "../context/AppContext";
import { PageHead } from "./Explore";
import { Panel } from "../components/State";

const USED = new Set(["WETH9", "USDG", "UniswapV3Factory", "QuoterV2", "SwapRouter02"]);

export default function Protocol() {
  const { config } = useApp();
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/chain/status").then((r) => setStatus(r.data)).catch(() => setStatus({})); }, []);
  return (
    <div className="mx-auto max-w-[1100px] px-4 lg:px-8 py-10 space-y-6" data-testid="protocol-page">
      <PageHead kicker="Transparency" title="Protocol & contracts">
        HIVEVORA is an independent interface. Swaps execute directly against Uniswap v3 contracts deployed on Robinhood Chain (verified via eth_getCode and SwapRouter02.factory()/WETH9() against developers.uniswap.org). HIVEVORA deploys no router, holds no user funds, and charges no swap fee.
      </PageHead>
      <Panel title="Network status">
        <div className="grid sm:grid-cols-2 gap-3 p-5 text-sm">
          {Object.entries(config.chains).map(([k, c]) => {
            const s = status?.[c.id];
            return (<div key={k} className="rounded-lg bg-black/30 p-4" data-testid={`chain-status-${c.id}`}>
              <p className="font-semibold text-white flex items-center gap-2">{s?.ok ? <CheckCircle2 className="h-4 w-4 text-lime" /> : <XCircle className="h-4 w-4 text-slate-500" />}{c.name}</p>
              <p className="text-xs font-mono text-slate-400 mt-1">chain {c.id} · {s?.ok ? `block ${s.block.toLocaleString()}` : "…"}</p>
              <p className="text-[11px] font-mono text-slate-500 break-all">{c.rpc}</p>
            </div>);
          })}
        </div>
      </Panel>
      <Panel title="Contracts (Robinhood Chain mainnet 4663)">
        <table className="w-full text-xs" data-testid="protocol-contracts"><tbody>
          {Object.entries(config.contracts).map(([n, a]) => (
            <tr key={n} className="border-b border-white/5">
              <td className="px-5 py-2.5 font-semibold text-slate-200">{n}</td>
              <td className="px-5 py-2.5"><a href={addrUrl(a)} target="_blank" rel="noreferrer" className="font-mono text-slate-400 hover:text-lime inline-flex items-center gap-1 break-all">{a} <ExternalLink className="h-3 w-3 shrink-0" /></a></td>
              <td className="px-5 py-2.5 text-right">{USED.has(n) ? <span className="text-lime font-bold">USED BY HIVEVORA</span> : <span className="text-slate-600">reference</span>}</td>
            </tr>))}
        </tbody></table>
      </Panel>
      <Panel title="Licensing & trademarks">
        <ul className="p-5 space-y-2 text-xs text-slate-400 leading-relaxed list-disc pl-9">
          <li>Uniswap v3-core: BUSL-1.1 (Change Date passed → GPL-2.0-or-later); v3-periphery & swap-router-contracts: GPL-2.0-or-later. HIVEVORA only calls deployed contracts and embeds ABIs — no redistribution of contract source.</li>
          <li>HIVEVORA’s interface is an original implementation (React + wagmi/viem, MIT). It is not a fork of the GPL-3.0 Uniswap interface, so no Uniswap UI code or brand assets are included.</li>
          <li>“Uniswap” is used only descriptively to identify the protocol. No Uniswap or Robinhood logos are used; no affiliation or endorsement is implied.</li>
          <li>Market data: GeckoTerminal public API (attribution shown on charts).</li>
        </ul>
      </Panel>
    </div>
  );
}
